import { BrandMark } from "@/components/shared/brand-mark";
import { Icon } from "@/components/ui/icon";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import type { Ref } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { api } from "@/lib/api";
import { roleLabels } from "@/lib/labels";
import { initials } from "@/lib/utils";

export function Header({
  onToggleDrawer,
  drawerOpen = false,
  menuButtonRef,
}: {
  onToggleDrawer: () => void;
  drawerOpen?: boolean;
  menuButtonRef?: Ref<HTMLButtonElement>;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  /* el contador se refresca solo: las notificaciones llegan por procesos del servidor */
  const unread = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: () => api<{ count: number }>("/notifications/my/unread-count"),
    enabled: Boolean(user),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const unreadCount = unread.data?.count ?? 0;
  if (!user) return null;

  return (
    <div
      className="navbar navbar-expand navbar-dark-pickled-bluewood scp-navbar"
      id="default-navbar"
      data-primary
    >
      <button
        ref={menuButtonRef}
        className="navbar-toggler scp-menu-toggle"
        type="button"
        aria-label="Abrir menú"
        aria-expanded={drawerOpen}
        aria-controls={drawerOpen ? "navigation-drawer" : undefined}
        aria-haspopup="dialog"
        onClick={onToggleDrawer}
      >
        <Icon name="short_text" className="" />
      </button>

      <Link
        to="/dashboard"
        className="navbar-brand scp-header-brand"
        aria-label="SCP — Inicio"
      >
        <span className="avatar avatar-sm navbar-brand-icon mr-0 mr-lg-8pt">
          <span className="avatar-title rounded bg-primary">
            <BrandMark />
          </span>
        </span>
        <span>SCP</span>
      </Link>

      <span className="scp-header-context">
        <span className="avatar avatar-sm mr-12pt">
          <span className="avatar-title rounded navbar-avatar">
            <Icon name="verified_user" className="" />
          </span>
        </span>
        <small className="flex d-flex flex-column">
          <strong className="navbar-text-100">
            {roleLabels[user.role] ?? user.role}
          </strong>
          <span className="navbar-text-50">
            {user.organizationMemberships?.find(
              (membership) => membership.isPrimary,
            )?.unit.name ?? "Capacitacion institucional"}
          </span>
        </small>
      </span>

      <form
        className="search-form navbar-search scp-header-search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const query = new FormData(event.currentTarget).get("q");
          const destination =
            user.role === "PARTICIPANT" ? "/catalog" : "/admin/courses";
          navigate(
            `${destination}?search=${encodeURIComponent(String(query ?? "").trim())}`,
          );
        }}
      >
        <button className="btn" type="submit" aria-label="Buscar">
          <Icon name="search" className="" />
        </button>
        <input
          type="search"
          name="q"
          aria-label="Buscar capacitaciones"
          className="form-control"
          placeholder="Buscar capacitaciones…"
        />
      </form>

      <div className="nav navbar-nav flex-nowrap d-flex scp-header-tools">
        <div className="nav-item">
          <Link
            to="/notifications"
            className="nav-link btn-flush position-relative"
            aria-label={
              unreadCount
                ? `Notificaciones: ${unreadCount} sin leer`
                : "Notificaciones"
            }
          >
            <Icon
              name={unreadCount ? "notifications_active" : "notifications_none"}
              className=""
            />
            {unreadCount > 0 && (
              <span
                className="badge badge-pill badge-accent"
                style={{
                  position: "absolute",
                  top: 2,
                  right: -2,
                  minWidth: 18,
                  fontSize: 10,
                  lineHeight: "14px",
                  padding: "2px 5px",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Link>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Menú de usuario"
              className="nav-item nav-link btn-flush d-flex align-items-center dropdown-toggle"
            >
              <span className="avatar avatar-sm mr-8pt2">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="avatar-img rounded-circle"
                  />
                ) : (
                  <span className="avatar-title rounded-circle bg-primary">
                    {initials(user.firstName, user.lastName)}
                  </span>
                )}
              </span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              collisionPadding={12}
              className="dropdown-menu dropdown-menu-right show scp-profile-menu"
            >
              <div className="dropdown-header">
                <strong>
                  {user.firstName} {user.lastName}
                </strong>
                <div className="text-50 small text-truncate">{user.email}</div>
              </div>
              <DropdownMenu.Item asChild className="dropdown-item">
                <Link to="/profile">Mi perfil</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild className="dropdown-item">
                <Link to="/notifications">Notificaciones</Link>
              </DropdownMenu.Item>
              <div className="dropdown-divider" />
              <DropdownMenu.Item
                className="dropdown-item text-danger"
                onSelect={() => void logout()}
              >
                Cerrar sesion
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
